<template>
    <WizardShell :mission="mission">
        <!-- Formulaire Horaire + Nom + Corridoring -->
        <div class="row g-3 justify-content-center align-items-start">
            <div class="col-auto">
                <div class="d-flex align-items-center gap-2">
                    <input v-model="scheduledHour" type="text" inputmode="numeric" maxlength="2"
                        class="form-control text-center" style="width: 70px;" placeholder="HH"
                        :disabled="$isViewer || saving || !canEditName" @input="onHourInput" />
                    <span>:</span>
                    <input v-model="scheduledMinute" type="text" inputmode="numeric" maxlength="2"
                        class="form-control text-center" style="width: 70px;" placeholder="MM"
                        :disabled="$isViewer || saving || !canEditName" @input="onMinuteInput" />
                </div>
            </div>
            <div class="col-auto">
                <input v-model="name" type="text" class="form-control" placeholder="Nom de mission"
                    :disabled="$isViewer || saving || !canEditName" />
            </div>
            <div class="col-auto">
                <select v-model="corridoringId" class="form-select"
                    :disabled="$isViewer || loadingCorridorings || saving || !canEditCorridoring">
                    <option disabled value="">— Sélectionnez un corridoring —</option>
                    <option v-for="c in corridorings" :key="c.id" :value="c.id">
                        {{ c.name }}
                    </option>
                </select>
                <div class="mt-2">
                    <label class="no-corridoring-option">
                        <input v-model="withoutCorridoring" type="checkbox"
                            :disabled="$isViewer || saving || !canEditCorridoring" />
                        <span>
                            Mission sans corridoring
                            <small>PREVOST LUXE uniquement</small>
                        </span>
                    </label>
                </div>
            </div>
        </div>
        <template #actions>
            <ButtonDisplay @click="onNext" :type="'start'" text="Suivant"
                :disabled="isDisabled || $isViewer || saving" />
        </template>
    </WizardShell>
</template>

<script>
import WizardShell from './WizardShell.vue'
import ButtonDisplay from '@/components/display/ButtonDisplay.vue'
import CorridoringService from '@/services/corridoring.service'
import MissionService from '@/services/mission.service'

export default {
    name: 'WizardNameCorridoring',
    props: {
        mission: { type: Object, default: null },
        // 'create' pour /mission/new, sinon 'edit'
        mode: { type: String, default: 'edit' }
    },
    components: { WizardShell, ButtonDisplay },
    data() {
        return {
            name: this.mission?.name || '',
            scheduledHour: this.mission?.scheduledHour || '',
            scheduledMinute: this.mission?.scheduledMinute || '',
            corridoringId: this.mission?.corridoringId || '',
            withoutCorridoring: !!this.mission?.withoutCorridoring,
            corridorings: [],
            loadingCorridorings: false,
            saving: false
        };
    },
    watch: {
        // pré-remplit quand la mission arrive (ou change)
        mission: {
            immediate: true,
            handler(m) {
                if (!m) return;
                if (!this.name && m.name) this.name = m.name;
                if (!this.scheduledHour && !this.scheduledMinute && (m.scheduledHour || m.scheduledMinute)) {
                    this.scheduledHour = m.scheduledHour || '';
                    this.scheduledMinute = m.scheduledMinute || '';
                }
                const incId = m.corridoringId ?? m.corridoring?.id;
                this.withoutCorridoring = !!m.withoutCorridoring;
                if (!this.corridoringId && incId != null) {
                    this.corridoringId = String(incId);
                }
            }
        }
    },
    computed: {
        isDisabled() {
            return !(this.name && this.name.trim()) || (!this.corridoringId && !this.withoutCorridoring);
        },
        // 🟢 Règles d’édition
        canEditName() {
            if (this.mode === 'create') return true;
            const s = this.mission?.status;
            return !['FINISHED', 'CANCELED'].includes(s);
        },
        canEditCorridoring() {
            if (this.mode === 'create') return true;
            const s = this.mission?.status;
            return !['FINISHED', 'CANCELED', 'ONGOING'].includes(s);
        }
    },
    mounted() {
        this.loadCorridorings();
    },
    methods: {
        onHourInput(e) {
            let value = String(e.target.value || '').replace(/\D/g, '').slice(0, 2);

            if (value === '') {
                this.scheduledHour = '';
                this.scheduledMinute = '';
                return;
            }

            if (value.length === 2) {
                value = String(Math.min(Number(value), 23)).padStart(2, '0');
            }

            this.scheduledHour = value;
        },
        onMinuteInput(e) {
            let value = String(e.target.value || '').replace(/\D/g, '').slice(0, 2);

            if (!this.scheduledHour) {
                this.scheduledMinute = '';
                return;
            }

            if (value.length === 2) {
                value = String(Math.min(Number(value), 59)).padStart(2, '0');
            }

            this.scheduledMinute = value;
        },
        buildScheduledTime() {
            if (!this.scheduledHour) {
                return null;
            }

            const hour = String(Number(this.scheduledHour)).padStart(2, '0');
            const minute = this.scheduledMinute === ''
                ? '00'
                : String(Number(this.scheduledMinute)).padStart(2, '0');

            return `${hour}:${minute}:00`;
        },
        async loadCorridorings() {
            try {
                this.loadingCorridorings = true;
                const { data } = await CorridoringService.getClientCorridorings();
                this.corridorings = (Array.isArray(data) ? data : []).map(c => ({
                    id: String(c.id),
                    name: c.name
                }));
                // Si on édite et que le corridoring courant n'est pas dans la liste, on l'insère (nom affichable)
                if (
                    this.mode !== 'create' &&
                    this.corridoringId &&
                    !this.corridorings.some(c => c.id === this.corridoringId) &&
                    this.mission?.corridoring?.name
                ) {
                    this.corridorings.unshift({ id: this.corridoringId, name: this.mission.corridoring.name });
                }
            } catch (e) {
                console.log(e);
            } finally {
                this.loadingCorridorings = false;
            }
        },
        async onNext() {
            if (this.isDisabled || this.$isViewer) return;
            try {
                this.saving = true;
                const payloadName = (this.name || '').trim();
                const scheduledTime = this.buildScheduledTime();
                const corrId = this.withoutCorridoring ? null : Number(this.corridoringId);
                if (this.mode === 'create') {
                    // Création : POST puis route suivante (vehicle/transporteur)
                    const { data } = await MissionService.createMission({
                        name: payloadName,
                        scheduledTime,
                        corridoringId: corrId,
                        withoutCorridoring: this.withoutCorridoring
                    });
                    if (!data?.id) throw new Error('Réponse de création invalide');
                    this.$router.replace({ name: 'MissionWizardTransporteur', params: { id: data.id } });
                } else {
                    // Édition : n'envoyer QUE ce qui est autorisé (et réellement modifié)
                    const payload = {};
                    if (this.canEditName && payloadName !== this.mission?.name) {
                        payload.name = payloadName;
                    }
                    if (this.canEditName && scheduledTime !== (this.mission?.scheduledTime || null)) {
                        payload.scheduledTime = scheduledTime;
                    }
                    const currentCorrIdValue = this.mission?.corridoringId ?? this.mission?.corridoring?.id ?? null;
                    const currentCorrId = currentCorrIdValue === null ? null : Number(currentCorrIdValue);
                    if (this.canEditCorridoring && corrId !== currentCorrId) {
                        payload.corridoringId = corrId;
                    }
                    if (this.canEditCorridoring && this.withoutCorridoring !== !!this.mission?.withoutCorridoring) {
                        payload.withoutCorridoring = this.withoutCorridoring;
                    }
                    if (Object.keys(payload).length > 0) {
                        await MissionService.transporteurUpdate(this.$route.params.id, payload);
                    }
                    this.$router.push({ name: 'MissionWizardTransporteur', params: { id: this.$route.params.id } });
                }
            } catch (e) {
                alert(e?.response?.data?.message || e.message || 'Erreur lors de la sauvegarde.');
            } finally {
                this.saving = false;
            }
        }
    }
}
</script>

<style scoped>
.no-corridoring-option {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border: 1px solid #dee2e6;
    border-radius: 0.75rem;
    background: #fff;
    cursor: pointer;
    user-select: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.no-corridoring-option input {
    width: 1.1rem;
    height: 1.1rem;
    margin: 0;
    cursor: pointer;
}

.no-corridoring-option span {
    display: flex;
    flex-direction: column;
    font-weight: 500;
    color: #212529;
    line-height: 1.2;
}

.no-corridoring-option small {
    margin-top: 0.15rem;
    font-size: 0.78rem;
    font-weight: 400;
    color: #6c757d;
}

.no-corridoring-option:has(input:disabled) {
    opacity: 0.65;
    cursor: not-allowed;
}
</style>